import { useEffect } from 'react';
import { previewThemeSlug } from './api/previewVisit';

/**
 * Keeps a theme preview inside itself.
 *
 * A preview is spelled entirely in the query — `?theme=<slug>` is what picks the theme and what
 * points the visit at the demo shop it is judged against. But every link a theme paints is a plain
 * shop route: `/?product=<slug>`, `/collections/all`, `/pages/terms`. Following one dropped the
 * query, so a merchant clicking a product inside a preview landed on the store's own installed
 * theme instead of the one they were looking at.
 *
 * So the preview's own keys are carried onto each in-shop link as it is clicked. The href is only
 * completed, never overruled: a link that already names a theme or a tenant keeps the one it names.
 */

/** The keys a preview visit is spelled with; a link that loses them stops being a preview. */
const CARRIED = ['theme', 'tenant', 'toolbar'] as const;

// Reports whether an anchor navigates inside this shop, so mailto:, tel: and downloads are left alone.
const isInShop = (anchor: HTMLAnchorElement): boolean =>
  !anchor.hasAttribute('download') &&
  anchor.protocol === window.location.protocol &&
  anchor.host === window.location.host;

// Completes one in-shop link with the preview keys it does not already carry.
const carryPreview = (anchor: HTMLAnchorElement, from: URLSearchParams): void => {
  const url = new URL(anchor.href);
  for (const key of CARRIED) {
    const value = from.get(key);
    if (value !== null && !url.searchParams.has(key)) url.searchParams.set(key, value);
  }
  anchor.href = url.toString();
};

// Carries the preview onto every in-shop link, so navigating a theme stays inside that theme.
export function usePreviewLinks(): void {
  useEffect(() => {
    if (!previewThemeSlug(window.location.search)) return;

    // Capture, so the href is complete before the browser reads it — and before a section's own
    // click handler can stop the event from ever reaching a listener that bubbles.
    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (anchor && isInShop(anchor)) carryPreview(anchor, new URLSearchParams(window.location.search));
    };

    document.addEventListener('click', onClick, true);
    document.addEventListener('auxclick', onClick, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('auxclick', onClick, true);
    };
  }, []);
}
